import { __awaiter } from "tslib";
import { addCustomFont } from './font-custom';
import { loadAllAssets } from 'shared/utils/assetLoader';
import { convertStylesToVariant, findInlineVariants } from './fonts';
import { textFromHtmlString } from './dom';
/**
 * Preload assets for Preview, Proof, and Live ad
 */
export const preloadAdAssets = (ad, currentLayoutId) => __awaiter(void 0, void 0, void 0, function* () {
    const scripts = ad.externalScripts;
    const stylesheets = ad.externalStylesheets;
    // lazily loads current condition's images only
    const currentConditionalStates = getConditionalStatesForCurrentLayout(ad.conditionalStates, currentLayoutId);
    const images = getImageAssets(currentConditionalStates);
    const webFontConfig = getWebFontConfig(currentConditionalStates, [], true);
    const allAssets = [
        ...mapAssetType(scripts, 'script'),
        ...mapAssetType(stylesheets, 'css'),
        ...mapAssetType(images, 'image'),
    ];
    return loadAllAssets(allAssets, webFontConfig);
});
const mapAssetType = (urls, type) => urls.map(url => ({ url, type }));
/**
 * Collects all images' urls from conditional states
 */
export const getImageAssets = (conditionalStates) => conditionalStates
    .map(cs => cs.styles.backgroundImage)
    .filter(image => image !== undefined);
/**
 * Filter an array of primitives by uniqueness.
 */
const unique = (value, index, self) => self.indexOf(value) === index;
const DEFAULT_FONT_CONFIG = {
    classes: false,
};
/**
 * Gets a Web Font Loader config for the given ConditionalStates.
 */
export const getWebFontConfig = (conditionalStates, extraVariants = [], optimized = false) => {
    const allTextBlocks = conditionalStates
        // Filter out those have font family and font source,
        // excluding `system` fonts.
        .filter(cs => cs.styles.fontFamily &&
        cs.attributes.fontSource &&
        cs.attributes.fontSource !== 'system')
        // Extract only relevant info
        .map(cs => ({
        content: cs.attributes.content,
        fontFamily: cs.styles.fontFamily,
        fontSource: cs.attributes.fontSource,
        fontStyle: cs.styles.fontStyle,
        fontWeight: cs.styles.fontWeight,
        textTransform: cs.styles.textTransform,
    }));
    const webFontConfig = allTextBlocks.reduce((carrier, textBlock) => {
        // Text Blocks using this Font Family and Source only.
        const familyTextBlocks = allTextBlocks.filter(t => t.fontFamily === textBlock.fontFamily &&
            t.fontSource === textBlock.fontSource);
        switch (textBlock.fontSource) {
            case 'google':
                return Object.assign(Object.assign({}, carrier), { google: addGoogleFont(carrier.google, textBlock, familyTextBlocks, optimized, extraVariants) });
            // Does not support adobe font at the moment
            case 'adobe': {
                return carrier;
            }
            // Custom font
            default: {
                return Object.assign(Object.assign({}, carrier), { custom: addCustomFont(carrier.custom, textBlock, allTextBlocks) });
            }
        }
    }, DEFAULT_FONT_CONFIG);
    if (webFontConfig === DEFAULT_FONT_CONFIG) {
        return undefined;
    }
    return webFontConfig;
};
/**
 * Adds an Google font the `google` block of a WebFont config.
 */
const addGoogleFont = (config = {
    families: [],
    text: '',
}, textBlock, familyTextBlocks, optimized, extraVariants) => {
    // Determine if we've already processed this Font Family.
    const found = config.families.some((fontVariant) => fontVariant.split(':')[0] === textBlock.fontFamily);
    // We've already processed this font family. Move along.
    if (found) {
        return config;
    }
    // Get all variants needed for this Font Family.
    const variants = familyTextBlocks
        .reduce((carrier, t) => {
        // Explicitly defined
        const variant = convertStylesToVariant(t.fontWeight, t.fontStyle);
        // Based on <i> and <b> tags.
        const inlineVariants = optimized
            ? findInlineVariants(t.content)
            : [];
        return [...carrier, variant, ...inlineVariants];
    }, extraVariants)
        .filter(unique);
    // Combine all variants into one Font Family string.
    const family = `${textBlock.fontFamily}:${variants.join(',')}`;
    // Add new unique characters to the subset of characters to fetch.
    const text = familyTextBlocks
        .reduce((mergedText, f) => {
        const blockText = optimized ? textFromHtmlString(f.content) : '';
        // Include necessary characters for text-transform of capitalize, uppercase, lowercase, etc.
        const withTextTransform = f.textTransform === 'uppercase' // Only include uppercase letters.
            ? blockText.toUpperCase()
            : f.textTransform === 'capitalize' // Include both untransformed and uppercase.
                ? `${blockText}${blockText.toUpperCase()}`
                : f.textTransform === 'lowercase' // Include only lowercase.
                    ? blockText.toLowerCase()
                    : blockText; // Return as-is.
        return mergedText + withTextTransform;
    }, config.text || '')
        .split('')
        .filter(unique)
        .join('');
    const result = {
        families: [...config.families, family],
        text,
    };
    return result;
};
/**
 * Extract only Conditional States for the given Layout
 */
export const getConditionalStatesForCurrentLayout = (conditionalStates, layoutId) => Object.keys(conditionalStates)
    .map(csKey => conditionalStates[csKey])
    .filter(cs => cs.layout === layoutId);
export default preloadAdAssets;
